/**
 * ADVANCES PAGE
 * Partner advance requests with PKR-first logic
 * Apple Liquid Glass Design System - WWDC25
 */

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  CreditCard,
  Plus,
  Clock,
  CheckCircle2,
  XCircle,
  DollarSign,
  ArrowRightCircle,
  AlertCircle,
  TrendingUp,
  Wallet,
  RefreshCw,
  Info,
  Sparkles
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { apiClient } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { GlassCard, ContentCard } from '../../components/ui/GlassCard'
import { Badge, StatusBadge } from '../../components/ui/Badge'
import { StatCard, StatCardGrid } from '../../components/ui/StatCard'
import { Skeleton, SkeletonStatCard, SkeletonCard } from '../../components/ui/Skeleton'
import { SegmentedControl } from '../../components/ui/SegmentedControl'

// Animation configurations
const springConfig = {
  type: "spring" as const,
  damping: 25,
  stiffness: 300
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: springConfig
  }
}

const listItemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: springConfig
  }
}

interface Advance {
  id: number
  partner_id: number
  partner_name: string
  amount: number
  currency: string
  reason: string
  status: 'Pending' | 'Approved' | 'Rejected' | 'Paid'
  source_pool: string | null
  pkr_used: number
  usd_used: number
  exchange_rate: number
  wise_fee: number
  approved_by_name: string | null
  approved_at: string | null
  payment_reference: string | null
  notes: string | null
  created_at: string
}

interface PoolStatus {
  pool: {
    pkr: number
    usd: number
    exchangeRate: number
  }
  pending: {
    pkr: number
    usd: number
  }
  myBalance: {
    pkr: number
    usd: number
  }
}

export function AdvancesPage() {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [advances, setAdvances] = useState<Advance[]>([])
  const [poolStatus, setPoolStatus] = useState<PoolStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [showApproveModal, setShowApproveModal] = useState(false)
  const [selectedAdvance, setSelectedAdvance] = useState<Advance | null>(null)
  const [filter, setFilter] = useState<string>('all')
  const [submitting, setSubmitting] = useState(false)

  // Form state for new advance
  const [formData, setFormData] = useState({
    amount: '',
    currency: 'PKR',
    reason: ''
  })

  // Approval form state
  const [approvalData, setApprovalData] = useState({
    exchange_rate: '280',
    notes: ''
  })

  const isAdmin = user?.type === 'Admin'

  useEffect(() => {
    if (user) {
      fetchData()
    }
  }, [user])

  const fetchData = async () => {
    console.log('Advances: fetchData called')
    setLoading(true)

    try {
      const [advancesData, poolData] = await Promise.all([
        apiClient.get('/advances'),
        apiClient.get('/advances/pool-status')
      ])
      console.log('Advances: API responses - advances:', advancesData, 'pool:', poolData)

      if (advancesData.success) {
        console.log('Advances: Setting advances data:', advancesData.data)
        setAdvances(advancesData.data)
      }

      if (poolData.success) {
        console.log('Advances: Setting pool status:', poolData.data)
        setPoolStatus(poolData.data)
      }
    } catch (err: any) {
      console.error('Advances: Fetch error:', err)
      setError(err.message || 'Failed to load data')
    } finally {
      setLoading(false)
      console.log('Advances: Loading complete')
    }
  }

  const formatCurrency = (amount: number, currency: string) => {
    if (currency === 'PKR') {
      return new Intl.NumberFormat('en-PK', {
        style: 'currency',
        currency: 'PKR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount)
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleSubmitRequest = async () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Please enter a valid amount')
      return
    }

    setSubmitting(true)
    try {
      const data = await apiClient.post('/advances', {
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        reason: formData.reason || undefined
      })

      if (data.success) {
        setShowModal(false)
        setFormData({ amount: '', currency: 'PKR', reason: '' })
        fetchData()
      } else {
        setError(data.error || 'Failed to submit request')
      }
    } catch (err: any) {
      setError(err.message || 'Network error submitting request')
    } finally {
      setSubmitting(false)
    }
  }

  const handleApprove = async () => {
    if (!selectedAdvance) return

    setSubmitting(true)
    try {
      const data = await apiClient.put(`/advances/${selectedAdvance.id}/approve`, {
        exchange_rate: parseFloat(approvalData.exchange_rate),
        notes: approvalData.notes || undefined
      })

      if (data.success) {
        setShowApproveModal(false)
        setSelectedAdvance(null)
        setApprovalData({ exchange_rate: '280', notes: '' })
        fetchData()
      } else {
        setError(data.error || 'Failed to approve')
      }
    } catch (err: any) {
      setError(err.message || 'Network error approving request')
    } finally {
      setSubmitting(false)
    }
  }

  const handleReject = async (advanceId: number) => {
    if (!confirm('Are you sure you want to reject this advance request?')) return

    try {
      const data = await apiClient.put(`/advances/${advanceId}/reject`, { notes: 'Rejected by admin' })

      if (data.success) {
        fetchData()
      } else {
        setError(data.error || 'Failed to reject')
      }
    } catch (err: any) {
      setError(err.message || 'Network error rejecting request')
    }
  }

  const handleMarkPaid = async (advanceId: number) => {
    const reference = prompt('Enter payment reference (optional):')

    try {
      const data = await apiClient.put(`/advances/${advanceId}/mark-paid`, { payment_reference: reference })

      if (data.success) {
        fetchData()
      } else {
        setError(data.error || 'Failed to mark as paid')
      }
    } catch (err: any) {
      setError(err.message || 'Network error')
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Pending': return 'warning'
      case 'Approved': return 'info'
      case 'Rejected': return 'error'
      case 'Paid': return 'success'
      default: return 'neutral'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Pending': return Clock
      case 'Approved': return CheckCircle2
      case 'Rejected': return XCircle
      case 'Paid': return DollarSign
      default: return Clock
    }
  }

  const filteredAdvances = advances.filter(a => {
    if (filter === 'all') return true
    return a.status.toLowerCase() === filter
  })

  const filterOptions = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: `Pending (${advances.filter(a => a.status === 'Pending').length})` },
    { value: 'approved', label: `Approved (${advances.filter(a => a.status === 'Approved').length})` },
    { value: 'paid', label: `Paid (${advances.filter(a => a.status === 'Paid').length})` },
    { value: 'rejected', label: `Rejected (${advances.filter(a => a.status === 'Rejected').length})` }
  ]

  console.log('Advances render - loading:', loading, 'error:', error, 'advances count:', advances.length, 'poolStatus:', !!poolStatus)

  // Loading state
  if (loading) {
    console.log('Advances: Rendering loading state')
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="p-8 space-y-8"
      >
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-9 w-48" />
            <Skeleton className="h-5 w-72" />
          </div>
          <div className="flex gap-3">
            <Skeleton className="h-11 w-11 rounded-xl" />
            <Skeleton className="h-11 w-40 rounded-xl" />
          </div>
        </div>

        {/* Stats grid skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <SkeletonStatCard />
          <SkeletonStatCard />
          <SkeletonStatCard />
          <SkeletonStatCard />
        </div>

        {/* Info card skeleton */}
        <SkeletonCard className="h-24" />

        {/* List skeleton */}
        <SkeletonCard className="h-96" />
      </motion.div>
    )
  }

  console.log('Advances: Rendering main content')

  return (
    <div className="p-8 space-y-8">
      {/* Page Header */}
      <motion.div className="flex items-center justify-between">
        <div>
          <h1 className="text-title1 font-bold text-light-text-primary dark:text-dark-text-primary mb-2">
            Advances
          </h1>
          <p className="text-body text-light-text-secondary dark:text-dark-text-secondary">
            {isAdmin ? 'Manage partner advance requests' : 'Request advances from your balance'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={fetchData}
            className="!p-3"
          >
            <RefreshCw className="w-5 h-5" />
          </Button>
          <Button
            onClick={() => setShowModal(true)}
            className="bg-gradient-to-br from-orange to-red"
          >
            <Plus className="w-5 h-5 mr-2" />
            Request Advance
          </Button>
        </div>
      </motion.div>

      {/* Error Alert */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-4 rounded-2xl bg-red/10 border border-red/20 flex items-center gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red flex-shrink-0" />
            <p className="text-body text-red flex-1">{error}</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setError(null)}
              className="text-red hover:bg-red/10"
            >
              Dismiss
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Balance Cards */}
      {poolStatus && (
        <motion.div>
          <StatCardGrid columns={isAdmin ? 4 : 3}>
            <StatCard
              title="My USD Balance"
              value={formatCurrency(poolStatus.myBalance.usd, 'USD')}
              icon={DollarSign}
              color="blue"
            />
            <StatCard
              title="My PKR Balance"
              value={formatCurrency(poolStatus.myBalance.pkr, 'PKR')}
              icon={Wallet}
              color="green"
            />
            {isAdmin && (
              <StatCard
                title="Pool PKR"
                value={formatCurrency(poolStatus.pool.pkr, 'PKR')}
                icon={Wallet}
                color="purple"
                badge={<Badge variant="green" size="sm">No fees</Badge>}
              />
            )}
            <StatCard
              title="USD/PKR Rate"
              value={poolStatus.pool.exchangeRate.toFixed(2)}
              icon={TrendingUp}
              color="orange"
            />
          </StatCardGrid>
        </motion.div>
      )}

      {/* PKR-First Info Card */}
      <motion.div>
        <GlassCard className="p-5 bg-gradient-to-br from-green/5 to-blue/5 border border-green/10">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-green/10 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-green" />
            </div>
            <div>
              <h3 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary mb-1">
                PKR-First Logic
              </h3>
              <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary">
                When requesting PKR, we use the native PKR pool first (no fees). If insufficient, USD is converted with Wise fees (~0.75%).
                This saves you money on every advance!
              </p>
            </div>
          </div>
        </GlassCard>
      </motion.div>

      {/* Filters */}
      <motion.div>
        <SegmentedControl
          options={filterOptions}
          value={filter}
          onChange={setFilter}
        />
      </motion.div>

      {/* Advances List */}
      <motion.div>
        <ContentCard>
          {filteredAdvances.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-light-text-quaternary/10 dark:bg-dark-text-quaternary/10 flex items-center justify-center">
                <CreditCard className="w-8 h-8 text-light-text-quaternary dark:text-dark-text-quaternary" />
              </div>
              <h3 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">
                No Advance Requests
              </h3>
              <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary">
                {filter === 'all' ? 'Start by requesting an advance' : `No ${filter} requests found`}
              </p>
            </div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="space-y-3"
            >
              {filteredAdvances.map((advance, index) => {
                const StatusIcon = getStatusIcon(advance.status)
                return (
                  <motion.div
                    key={advance.id}
                    variants={listItemVariants}
                    whileHover={{ scale: 1.01, x: 4 }}
                    className="p-4 bg-white/30 dark:bg-white/5 rounded-xl border border-black/[0.04] dark:border-white/[0.04]"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          advance.status === 'Pending' ? 'bg-orange/10 text-orange' :
                          advance.status === 'Approved' ? 'bg-blue/10 text-blue' :
                          advance.status === 'Rejected' ? 'bg-red/10 text-red' :
                          'bg-green/10 text-green'
                        }`}>
                          <StatusIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-body font-semibold text-light-text-primary dark:text-dark-text-primary">
                            {formatCurrency(advance.amount, advance.currency)}
                          </p>
                          {isAdmin && (
                            <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                              {advance.partner_name}
                            </p>
                          )}
                        </div>
                      </div>
                      <StatusBadge status={getStatusBadge(advance.status) as any}>
                        {advance.status}
                      </StatusBadge>
                    </div>

                    {advance.reason && (
                      <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary mb-3">
                        {advance.reason}
                      </p>
                    )}

                    {/* Source pool info for approved/paid */}
                    {(advance.status === 'Approved' || advance.status === 'Paid') && advance.source_pool && (
                      <div className="flex items-center gap-4 text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary mb-3 p-3 bg-white/20 dark:bg-white/5 rounded-lg">
                        <span className="flex items-center gap-1">
                          <Info className="w-3 h-3" />
                          Source: {advance.source_pool}
                        </span>
                        {advance.pkr_used > 0 && <span>PKR: {formatCurrency(advance.pkr_used, 'PKR')}</span>}
                        {advance.usd_used > 0 && <span>USD: {formatCurrency(advance.usd_used, 'USD')}</span>}
                        {advance.wise_fee > 0 && <span className="text-orange">Fee: ${advance.wise_fee.toFixed(2)}</span>}
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                        {formatDate(advance.created_at)}
                      </p>

                      {/* Admin actions */}
                      {isAdmin && advance.status === 'Pending' && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => {
                              setSelectedAdvance(advance)
                              setApprovalData({
                                exchange_rate: poolStatus?.pool.exchangeRate.toString() || '280',
                                notes: ''
                              })
                              setShowApproveModal(true)
                            }}
                            className="bg-green hover:bg-green/90"
                          >
                            Approve
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleReject(advance.id)}
                            className="bg-red hover:bg-red/90"
                          >
                            Reject
                          </Button>
                        </div>
                      )}

                      {isAdmin && advance.status === 'Approved' && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleMarkPaid(advance.id)}
                        >
                          <ArrowRightCircle className="w-4 h-4 mr-1" />
                          Mark Paid
                        </Button>
                      )}
                    </div>
                  </motion.div>
                )
              })}
            </motion.div>
          )}
        </ContentCard>
      </motion.div>

      {/* New Advance Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Request Advance"
      >
        <div className="space-y-6">
          {/* Balance Info */}
          {poolStatus && (
            <div className="p-4 rounded-xl bg-blue/10 border border-blue/20">
              <p className="text-caption1 text-light-text-secondary dark:text-dark-text-secondary mb-2">Your Available Balance:</p>
              <div className="flex items-center gap-4">
                <Badge variant="blue">{formatCurrency(poolStatus.myBalance.usd, 'USD')}</Badge>
                <Badge variant="green">{formatCurrency(poolStatus.myBalance.pkr, 'PKR')}</Badge>
              </div>
            </div>
          )}

          {/* Currency Selection */}
          <div>
            <label className="block text-subheadline font-medium text-light-text-secondary dark:text-dark-text-secondary mb-3">
              Currency
            </label>
            <div className="grid grid-cols-2 gap-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setFormData({ ...formData, currency: 'PKR' })}
                className={`p-4 rounded-xl border-2 transition-all ${
                  formData.currency === 'PKR'
                    ? 'border-green bg-green/10'
                    : 'border-black/10 dark:border-white/10 hover:border-black/20 dark:hover:border-white/20'
                }`}
              >
                <span className="text-headline font-bold text-light-text-primary dark:text-dark-text-primary">PKR</span>
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary mt-1">No fees from PKR pool</p>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setFormData({ ...formData, currency: 'USD' })}
                className={`p-4 rounded-xl border-2 transition-all ${
                  formData.currency === 'USD'
                    ? 'border-blue bg-blue/10'
                    : 'border-black/10 dark:border-white/10 hover:border-black/20 dark:hover:border-white/20'
                }`}
              >
                <span className="text-headline font-bold text-light-text-primary dark:text-dark-text-primary">USD</span>
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary mt-1">Direct from USD pool</p>
              </motion.button>
            </div>
          </div>

          {/* Amount */}
          <Input
            label={`Amount (${formData.currency})`}
            type="number"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            placeholder={formData.currency === 'PKR' ? '50000' : '500'}
          />

          {/* Reason */}
          <div>
            <label className="block text-subheadline font-medium text-light-text-secondary dark:text-dark-text-secondary mb-2">
              Reason (Optional)
            </label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="What is this advance for?"
              rows={3}
              className="w-full px-4 py-3 bg-white/50 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue/50 resize-none text-body"
            />
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmitRequest}
            disabled={submitting}
            className="w-full bg-gradient-to-br from-orange to-red"
          >
            {submitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </div>
      </Modal>

      {/* Approve Modal (Admin) */}
      <Modal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        title="Approve Advance"
      >
        {selectedAdvance && (
          <div className="space-y-6">
            {/* Request Info */}
            <div className="p-4 rounded-xl bg-blue/10 border border-blue/20">
              <p className="text-title2 font-bold text-blue mb-1">
                {formatCurrency(selectedAdvance.amount, selectedAdvance.currency)}
              </p>
              <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary">
                Requested by {selectedAdvance.partner_name}
              </p>
              {selectedAdvance.reason && (
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary mt-2 italic">
                  "{selectedAdvance.reason}"
                </p>
              )}
            </div>

            {/* PKR-First Preview */}
            {selectedAdvance.currency === 'PKR' && poolStatus && (
              <div className="p-4 rounded-xl bg-green/10 border border-green/20">
                <p className="text-subheadline font-medium text-green mb-2">Source Calculation:</p>
                {selectedAdvance.amount <= poolStatus.pool.pkr ? (
                  <p className="text-caption1 text-light-text-secondary dark:text-dark-text-secondary">
                    Entire amount from PKR pool (no fees)
                  </p>
                ) : poolStatus.pool.pkr > 0 ? (
                  <div className="text-caption1 text-light-text-secondary dark:text-dark-text-secondary space-y-1">
                    <p>PKR from pool: {formatCurrency(poolStatus.pool.pkr, 'PKR')}</p>
                    <p>USD to convert: ${((selectedAdvance.amount - poolStatus.pool.pkr) / parseFloat(approvalData.exchange_rate)).toFixed(2)}</p>
                    <p className="text-orange">Est. fee: ~${(((selectedAdvance.amount - poolStatus.pool.pkr) / parseFloat(approvalData.exchange_rate)) * 0.0075).toFixed(2)}</p>
                  </div>
                ) : (
                  <div className="text-caption1 text-light-text-secondary dark:text-dark-text-secondary space-y-1">
                    <p>All from USD conversion</p>
                    <p>USD needed: ${(selectedAdvance.amount / parseFloat(approvalData.exchange_rate)).toFixed(2)}</p>
                    <p className="text-orange">Est. fee: ~${((selectedAdvance.amount / parseFloat(approvalData.exchange_rate)) * 0.0075).toFixed(2)}</p>
                  </div>
                )}
              </div>
            )}

            {/* Exchange Rate */}
            <Input
              label="Exchange Rate (USD to PKR)"
              type="number"
              value={approvalData.exchange_rate}
              onChange={(e) => setApprovalData({ ...approvalData, exchange_rate: e.target.value })}
              step="0.01"
            />

            {/* Notes */}
            <div>
              <label className="block text-subheadline font-medium text-light-text-secondary dark:text-dark-text-secondary mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={approvalData.notes}
                onChange={(e) => setApprovalData({ ...approvalData, notes: e.target.value })}
                placeholder="Any notes for this approval..."
                rows={2}
                className="w-full px-4 py-3 bg-white/50 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue/50 resize-none text-body"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setShowApproveModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleApprove}
                disabled={submitting}
                className="flex-1 bg-gradient-to-br from-green to-blue"
              >
                {submitting ? 'Approving...' : 'Approve'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
